import React from 'react';
import { VALID_PRECISIONS } from '../constants.js';

/**
 * DatingEntryEditor - Sub-form for adding/removing Dating_Entries.
 *
 * Props:
 * - datings: array of { startYear, endYear, precision, label, argument } objects
 * - primaryDating: number (index into datings array)
 * - onChange(datings): callback when datings change
 * - onPrimaryChange(index): callback when primary dating selection changes
 *
 * Functionality:
 * - Add new dating entry button
 * - Remove dating entry button per entry
 * - Each entry has: startYear (number input), endYear (number input, optional),
 *   precision (select from VALID_PRECISIONS), label (text input), argument (textarea)
 * - Radio button per entry to set as primary dating
 */
function DatingEntryEditor({ datings = [], primaryDating = 0, onChange, onPrimaryChange }) {
  function handleAddDating() {
    const newEntry = {
      startYear: '',
      endYear: '',
      precision: '',
      label: '',
      argument: '',
    };
    onChange([...datings, newEntry]);
  }

  function handleRemoveDating(index) {
    const updated = datings.filter((_, i) => i !== index);
    onChange(updated);
    // Adjust primaryDating if needed
    if (primaryDating >= updated.length && updated.length > 0) {
      onPrimaryChange(updated.length - 1);
    } else if (updated.length === 0) {
      onPrimaryChange(0);
    } else if (primaryDating > index) {
      onPrimaryChange(primaryDating - 1);
    }
  }

  function handleFieldChange(index, field, value) {
    const updated = datings.map((entry, i) =>
      i === index ? { ...entry, [field]: value } : entry
    );
    onChange(updated);
  }

  function handlePrimaryChange(index) {
    onPrimaryChange(index);
  }

  return (
    <div className="dating-entry-editor">
      {datings.length === 0 && (
        <p className="dating-entry-empty">No dating entries yet.</p>
      )}

      {datings.map((entry, index) => (
        <div key={index} className="dating-entry-item" data-testid={`dating-entry-${index}`}>
          <div className="dating-entry-header">
            <label className="dating-entry-primary-label">
              <input
                type="radio"
                name="primaryDating"
                checked={primaryDating === index}
                onChange={() => handlePrimaryChange(index)}
                aria-label={`Set dating ${index + 1} as primary`}
              />
              <span className="dating-entry-number">Dating #{index + 1}</span>
              {primaryDating === index && (
                <span className="dating-entry-primary-badge">Primary</span>
              )}
            </label>
            <button
              type="button"
              className="dating-entry-remove-btn"
              onClick={() => handleRemoveDating(index)}
              aria-label={`Remove dating ${index + 1}`}
            >
              ✕
            </button>
          </div>

          <div className="dating-entry-fields">
            <div className="dating-entry-row">
              <div className="dating-entry-field">
                <label htmlFor={`dating-start-year-${index}`}>Start Year *</label>
                <input
                  id={`dating-start-year-${index}`}
                  type="number"
                  value={entry.startYear}
                  onChange={(e) => handleFieldChange(index, 'startYear', e.target.value)}
                  placeholder="e.g., -586"
                />
              </div>

              <div className="dating-entry-field">
                <label htmlFor={`dating-end-year-${index}`}>End Year</label>
                <input
                  id={`dating-end-year-${index}`}
                  type="number"
                  value={entry.endYear}
                  onChange={(e) => handleFieldChange(index, 'endYear', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="dating-entry-field">
              <label htmlFor={`dating-precision-${index}`}>Precision</label>
              <select
                id={`dating-precision-${index}`}
                value={entry.precision}
                onChange={(e) => handleFieldChange(index, 'precision', e.target.value)}
              >
                <option value="">-- Select --</option>
                {VALID_PRECISIONS.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="dating-entry-field">
              <label htmlFor={`dating-label-${index}`}>Label</label>
              <input
                id={`dating-label-${index}`}
                type="text"
                value={entry.label}
                onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                placeholder="e.g., Early dating, Late dating"
              />
            </div>

            <div className="dating-entry-field">
              <label htmlFor={`dating-argument-${index}`}>Argument</label>
              <textarea
                id={`dating-argument-${index}`}
                value={entry.argument}
                onChange={(e) => handleFieldChange(index, 'argument', e.target.value)}
                placeholder="Scholarly argument for this dating"
                rows={3}
              />
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="dating-entry-add-btn"
        onClick={handleAddDating}
      >
        + Add Dating Entry
      </button>
    </div>
  );
}

export default DatingEntryEditor;
